import { useState } from 'react';
import { Info } from 'lucide-react';
import { fmt, fmtCount } from '../utils/formatters';

/**
 * The outcomes strip on the brief page.
 *
 * It used to headline lifetime_found_dollars as money we had located for the
 * merchant — a running sum of credits floored at zero. Flooring
 * a symmetric quantity at zero has a positive expected value, so that number
 * climbed on noise alone, roughly $50 per measured product move even when a
 * move changed nothing. It is still in the database and on the admin pulse.
 * It is no longer shown to a merchant as money we found them.
 *
 * What it shows instead is countable: moves adopted, moves measured, and the
 * net RAW change across them — which goes negative when the fortnight after a
 * move was worse than the fortnight before, because sometimes it was.
 */
export default function LedgerCounter({ adoptedCount, measuredCount, measuredNetRaw, didntPanOut, streakDays }: {
  adoptedCount: number;
  measuredCount: number;
  measuredNetRaw: number;
  didntPanOut?: number;
  streakDays: number;
}) {
  const [showHow, setShowHow] = useState(false);
  const adopted = adoptedCount || 0;
  const measured = measuredCount || 0;
  const net = measuredNetRaw || 0;
  const missed = didntPanOut || 0;
  const streak = streakDays || 0;
  const signedNet = fmt(net);

  return (
    <div className="bg-[#4A2D5E] rounded-[14px] p-6 shadow-card">
      {measured > 0 ? (
        <div className="font-serif text-2xl text-[#FAF9F7]">
          {measured} measured move{measured === 1 ? '' : 's'}:{' '}
          <span className={`tabular-nums ${net < 0 ? 'text-[#E3B7B7]' : ''}`}>net {signedNet}</span>
        </div>
      ) : adopted > 0 ? (
        <div className="font-serif text-xl text-[#FAF9F7]">
          {adopted} move{adopted === 1 ? '' : 's'} adopted, none measured yet
        </div>
      ) : (
        <div className="font-serif text-xl text-[#FAF9F7]">Nothing measured yet</div>
      )}

      {missed > 0 && (
        <div className="font-sans text-xs text-[#FAF9F7]/70 mt-1.5">
          {missed} didn&rsquo;t pan out
        </div>
      )}

      {/* How it is measured stays one tap away, not always on screen
          (Lucas, 2026-09-24: "Apple doesn't explain themselves"). */}
      <button
        type="button"
        onClick={() => setShowHow((v) => !v)}
        aria-expanded={showHow}
        aria-label="How this is measured"
        className="mt-1 -ml-3.5 w-11 h-11 inline-flex items-center justify-center rounded-[8px] text-[#FAF9F7]/70 hover:text-[#FAF9F7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
      >
        <Info size={14} strokeWidth={1.5} />
      </button>
      {showHow && (
        <div className="font-sans text-xs text-[#FAF9F7]/70 max-w-md">
          {measured > 0
            ? 'Measured against the 14 days before each move. Moves that didn\u2019t pan out count at what they actually did. This is not a controlled test.'
            : adopted > 0
              ? 'Measured two weeks after you adopt a move. This is not a controlled test.'
              : 'Adopt a move and we measure what happens. This is not a controlled test.'}
        </div>
      )}

      {streak >= 3 && (
        <div className="font-sans text-sm text-[#FAF9F7]/80 mt-2.5">
          {fmtCount(streak)}-day streak
        </div>
      )}
    </div>
  );
}
